import { List, Map } from "immutable";

// @ts-ignore
import { updateSpacingValue } from "@seneca/spacing";

import SessionFinishedEvent from "seneca-common/features/session/models/SessionFinishedEvent";
import objectToJS from "seneca-common/utils/functions/immutable/props-to-js";
import { logMessage } from "seneca-common/utils/sentry/logError";

import actionTypes from "../../../../action-types";
import {
  SectionStat,
  SectionStats
} from "../../../../models/state/sectionStats";
import transformServerSessionDuration from "../../../../utils/transformServerSessionDuration";
import transformServerTimestamp from "../../../../utils/transformServerTimestamp";
import { updateAverageScore } from "../../../../utils/updateAverageScore";
import { updateBestScore } from "../../../../utils/updateBestScore";

export const sectionStats = (
  sectionStats: SectionStats = Map<string, SectionStat>(),
  action: Record<string, any>
): SectionStats => {
  switch (action.type) {
    case actionTypes.COMPLETE_SESSION: {
      const sectionId = getSessionSectionId(action.payload);
      return sectionStats.update(sectionId, meta =>
        individualSectionStat(meta, action)
      );
    }

    case actionTypes.COMPLETE_NOTES_ONLY_SESSION: {
      const sectionId = getSessionSectionId(action.payload);
      return sectionStats.update(sectionId, sectionStat => {
        return applyCompleNotesOnlyteSessionEventToSectionStat(
          sectionStat,
          action.payload
        );
      });
    }

    case actionTypes.RECEIVE_SECTION_STUDY_STATS:
      const newStats = action.payload.sectionStudyStats.reduce(
        (newStats: any, stat: any) => {
          return newStats.set(stat.sectionId, transformServerSectionStat(stat));
        },
        Map<string, SectionStat>()
      );
      return sectionStats.merge(newStats);

    case actionTypes.RECEIVE_RECONCILED_SECTION_STUDY_STATS: {
      const newStats = action.payload.sectionStudyStats.reduce(
        (newStats: any, stat: any) => {
          return newStats.set(stat.sectionId, new SectionStat(stat));
        },
        Map<string, SectionStat>()
      );
      return sectionStats.merge(newStats);
    }

    default:
      return sectionStats;
  }
};

export const individualSectionStat = (
  sectionStat: SectionStat = new SectionStat(),
  action: Record<string, any>
) => {
  switch (action.type) {
    case actionTypes.COMPLETE_SESSION:
      return applyCompleteSessionEventToSectionStat(
        sectionStat,
        action.payload
      );

    default:
      return sectionStat;
  }
};

export const applyCompleteSessionEventToSectionStat = (
  sectionStat: SectionStat,
  sessionEvent: SessionFinishedEvent,
  {
    logUpdateSpacingWarnings = true
  }: {
    logUpdateSpacingWarnings?: boolean;
  } = {}
) => {
  const {
    timeStarted,
    timeFinished,
    modulesStudied,
    averageScore,
    endingProficiency,
    sessionScore,
    wrongModuleIds,
    totalModulesStudyTime
  } = sessionEvent;

  const sectionId = getSessionSectionId(sessionEvent);
  const { lastStudied, sessionsCompleted } = sectionStat;
  return sectionStat.withMutations(sectionStat =>
    sectionStat
      .set("sectionId", sectionId)
      .set("sessionsCompleted", sessionsCompleted + 1)
      .update(
        "totalStudyTime",
        totalStudyTime => totalStudyTime + totalModulesStudyTime
      )
      .set("lastStudied", timeFinished!)
      .update("sessionModulesStudied", studied => studied + modulesStudied)
      .update("averageScore", avg =>
        updateAverageScore(avg, sessionsCompleted + 1, averageScore!)
      )
      .set("lastScore", sessionScore)
      .update("bestScore", bestScore =>
        updateBestScore(bestScore, sessionScore)
      )
      .update("spacing", s =>
        updateSpacingValue(
          s,
          lastStudied,
          timeFinished,
          logUpdateSpacingWarnings &&
            function (message: string, meta: Record<string, any>) {
              logMessage(
                message,
                objectToJS({
                  ...meta,
                  sectionStat,
                  timeStarted,
                  timeFinished,
                  modulesStudied,
                  averageScore,
                  endingProficiency
                }) as any
              );
            }
        )
      )
      .set("proficiency", endingProficiency)
      .set("moduleIdsIncorrectLast", wrongModuleIds)
  );
};

export const applyCompleNotesOnlyteSessionEventToSectionStat = (
  sectionStat: SectionStat = new SectionStat(),
  sessionEvent: SessionFinishedEvent,
  {
    logUpdateSpacingWarnings = true
  }: {
    logUpdateSpacingWarnings?: boolean;
  } = {}
) => {
  const { timeFinished } = sessionEvent;

  const sectionId = getSessionSectionId(sessionEvent);
  const { numberOfNotesOnlySessions } = sectionStat;
  return sectionStat.withMutations(sectionStat =>
    sectionStat
      .set("sectionId", sectionId)
      .set("numberOfNotesOnlySessions", numberOfNotesOnlySessions + 1)
      .set("lastStudied", timeFinished)
  );
};

export const transformServerSectionStat = (
  serverSectionStat: Record<string, any>
) =>
  new SectionStat({
    ...serverSectionStat,
    totalStudyTime: transformServerSessionDuration(
      serverSectionStat.totalStudyTime
    ),
    lastStudied: transformServerTimestamp(serverSectionStat.lastStudied),
    moduleIdsStudied: List(serverSectionStat.moduleIdsStudied),
    moduleIdsIncorrectLast: List(serverSectionStat.moduleIdsIncorrectLast)
  });

function getSessionSectionId(sessionEvent: SessionFinishedEvent) {
  return sessionEvent.sectionIds.first() as string;
}
